import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { CheckCircle2, Clock, ExternalLink, XCircle } from 'lucide-react';
import { api, apiError } from '@/lib/api';
import { haptic, openExternal, showConfirm } from '@/lib/telegram';
import { dateTime, minutesLeft, money } from '@/lib/format';
import { useI18n } from '@/i18n';
import { useAuth } from '@/context/AuthContext';
import { CopyRow, InfoRow, MethodIcon, Notice, PageLoading, Spinner, StatusPill } from '@/components/ui';
import type { Tx } from '@/types/api';

const KNOWN_REASONS = new Set(['cancelled_by_user', 'deposit_window_expired', 'fulgurpay_failed', 'fulgurfix_failed', 'fulgurfix_payout_failed', 'fulgurpay_payout_failed', 'apay_payout_failed', 'zerone_payout_failed', 'zicharge_payout_failed']);

/** One request: status (refreshed every 5 s while pending), payment page / address, cancel. */
export function TxPage() {
    const { id } = useParams();
    const { t, te, lang } = useI18n();
    const { me } = useAuth();
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const q = useQuery({
        queryKey: ['tx', id],
        queryFn: async () => (await api.get<Tx>(`/transactions/${encodeURIComponent(id || '')}`)).data,
        refetchInterval: query => (query.state.data && query.state.data.status === 'pending' ? 5000 : false)
    });
    if (q.isLoading) return <PageLoading />;
    if (q.isError || !q.data) return <Notice tone="warn">{te(apiError(q.error))}</Notice>;
    const tx = q.data;
    const board = me?.currency || 'BRL';
    const pendingDeposit = tx.type === 'deposit' && tx.status === 'pending';
    const left = minutesLeft(tx.expiresAt);

    const cancel = async () => {
        if (!(await showConfirm(t('tx.cancelConfirm')))) return;
        setBusy(true); setError(null);
        try {
            const { data } = await api.post<Tx>(`/deposits/${encodeURIComponent(tx.operationId)}/cancel`);
            qc.setQueryData(['tx', id], data);
            void qc.invalidateQueries({ queryKey: ['transactions'] });
            haptic('success');
        } catch (e) { setError(te(apiError(e))); haptic('error'); }
        finally { setBusy(false); }
    };

    const StatusIcon = tx.status === 'confirmed' ? CheckCircle2 : tx.status === 'rejected' ? XCircle : Clock;
    const statusColor = tx.status === 'confirmed' ? 'text-brand-300' : tx.status === 'rejected' ? 'text-red-300' : 'text-amber-200';
    return (
        <div className="space-y-3">
            <div className="card-lime flex items-center gap-3">
                <MethodIcon kind={tx.kind} size="lg" />
                <div className="flex-1 min-w-0">
                    <div className="text-xs text-ink-muted">{t(`tx.type.${tx.type}`)} · {tx.method}</div>
                    <div className="text-2xl font-black font-display">{money(tx.amount, tx.currency, lang)}</div>
                    <div className="mt-1"><StatusPill status={tx.status} /></div>
                </div>
                <StatusIcon className={`w-8 h-8 ${statusColor}`} />
            </div>

            <Notice tone={tx.status === 'confirmed' ? 'ok' : tx.status === 'rejected' ? 'warn' : 'info'}>
                {t(`tx.explain.${tx.type}.${tx.status}`, { reason: tx.rejectionReason ? (KNOWN_REASONS.has(tx.rejectionReason) ? t(`tx.reasons.${tx.rejectionReason}`) : tx.rejectionReason) : '' })}
            </Notice>

            {pendingDeposit && tx.payUrl && (
                <button className="btn-teal w-full py-4 text-base" onClick={() => openExternal(tx.payUrl as string)}>
                    <ExternalLink className="w-5 h-5" /> {tx.kind === 'pix' ? t('tx.payPix') : t('tx.openPayment')}
                </button>
            )}

            {pendingDeposit && tx.crypto && (
                <div className="space-y-2">
                    {tx.qr && <div className="card flex justify-center py-5"><img src={tx.qr} alt="QR" className="w-48 h-48 rounded-xl bg-white p-2" /></div>}
                    {tx.crypto.cryptoAmount && <CopyRow label={t('tx.sendExactly', { coin: tx.crypto.label })} value={String(tx.crypto.cryptoAmount)} />}
                    <CopyRow label={t('tx.address', { network: tx.crypto.network })} value={tx.crypto.address} />
                    <Notice tone="warn">{t('tx.cryptoWarning', { coin: tx.crypto.label, network: tx.crypto.network })}</Notice>
                    {tx.crypto.url && <button className="btn-secondary w-full" onClick={() => openExternal(tx.crypto!.url as string)}><ExternalLink className="w-4 h-4" /> {t('tx.openPayment')}</button>}
                </div>
            )}

            {pendingDeposit && tx.expiresAt && (
                <p className="text-xs text-center text-ink-muted">{left > 0 ? t('tx.expiresIn', { minutes: left }) : t('tx.expired')}</p>
            )}

            <div className="card py-3">
                {tx.credit != null && tx.currency !== board && <InfoRow label={tx.type === 'deposit' ? t('tx.credit') : t('tx.debit')} value={money(tx.credit, board, lang)} strong />}
                {tx.bonus > 0 && <InfoRow label={t('tx.bonus')} value={`${money(tx.bonus, board, lang)}${tx.bonusPercent ? ` (${tx.bonusPercent}%)` : ''}`} />}
                {tx.type === 'withdraw' && tx.fee != null && tx.fee > 0 && <InfoRow label={t('tx.fee')} value={money(tx.fee, tx.currency, lang)} />}
                {tx.type === 'withdraw' && tx.finalAmount != null && <InfoRow label={t('tx.youReceive')} value={money(tx.finalAmount, tx.currency, lang)} strong />}
                {tx.destination && <InfoRow label={t('tx.destination')} value={<span className="font-mono text-xs break-all">{tx.destination}</span>} />}
                <InfoRow label={t('tx.date')} value={dateTime(tx.createdAt, lang)} />
                {tx.processedAt && <InfoRow label={t('tx.processed')} value={dateTime(tx.processedAt, lang)} />}
            </div>
            <CopyRow label={t('tx.operationId')} value={tx.operationId} />

            {error && <Notice tone="warn">{error}</Notice>}
            {pendingDeposit && tx.cancellable && (
                <button className="btn-danger w-full" disabled={busy} onClick={cancel}>{busy ? <Spinner className="w-4 h-4" /> : <XCircle className="w-4 h-4" />} {t('tx.cancel')}</button>
            )}
            <button className="btn-secondary w-full" onClick={() => navigate('/history')}>{t('tx.allRequests')}</button>
        </div>
    );
}
